import React from "react";
import Alert from "@material-ui/lab/Alert";
import PulsingDot from "@common/pulsing-dot";
import { locale } from "@bonliva-crm/locale";
import { makeStyles } from "@material-ui/core";
import { format } from "date-fns";
import { sv } from "date-fns/locale";
import {
  BookingProfile_GetLastCreateBookingKitWfStatusQuery,
  WorkflowStatus
} from "@graphql";

type Props = {
  data: BookingProfile_GetLastCreateBookingKitWfStatusQuery | null;
};

type NormalizedStatus = "Running" | "Failed" | "Completed";

export function BookingKitAutomationStatus({ data }: Props) {
  const classes = useStyles();
  if (!data) return null;

  const {
    status: wfStatus,
    result,
    scheduledAt,
    completedAt
  } = data.getLastWorkflowRun || {};

  const normalized: NormalizedStatus | null = (() => {
    if (wfStatus === WorkflowStatus.Running) return "Running";
    if (wfStatus === WorkflowStatus.Failed) return "Failed";
    if (wfStatus === WorkflowStatus.Completed) {
      if (result?.data?.status === "failed") return "Failed";
      if (result?.data?.status === "completed") return "Completed";
    }
    return null;
  })();

  if (!normalized) return null;

  const severity: "info" | "error" | "success" =
    normalized === "Running" ? "info" : normalized === "Failed" ? "error" : "success";

  const text = (() => {
    switch (normalized) {
      case "Running":
        return {
          titleKey: "Processing pending",
          descKey: "Your request is being handled. Please wait until it's complete."
        };
      case "Failed":
        return {
          titleKey: "Kit creation failed!",
          descKey: result?.data?.message || "An error occurred during processing."
        };
      case "Completed":
        return {
          titleKey: "Kit created!",
          descKey: result?.data?.message || "The process completed successfully."
        };
    }
  })();

  // Pick which date to show
  const dateToShow = normalized === "Running" ? scheduledAt : completedAt;

  const formatSwedishDate = (d?: string | number | Date) => {
    if (!d) return null;
    try {
      return format(new Date(d), "d MMM yyyy, HH:mm", { locale: sv });
    } catch {
      return null;
    }
  };

  const formatted = formatSwedishDate(dateToShow);

  return (
    <Alert
      icon={
        <div className={classes.iconWrapper}>
          <PulsingDot variant={severity} />
        </div>
      }
      severity={severity}
      className={classes.alert}
    >
      <strong>{locale[text.titleKey] ?? text.titleKey}</strong>
      <br />
      <div style={{ marginTop: 4 }}>{locale[text.descKey] ?? text.descKey}</div>
      {formatted && (
        <div className={classes.meta}>
          {locale["Last updated"] ?? "Senast uppdaterad"}: {formatted}
        </div>
      )}
    </Alert>
  );
}

const useStyles = makeStyles(() => ({
  alert: {
    borderRadius: 0
  },
  iconWrapper: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    width: 21
  },
  meta: {
    marginTop: 4,
    fontSize: 12,
    opacity: 0.8
  }
}));
